import { motion } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { useAuroraTheme } from './useAuroraTheme';
import type { ReactNode } from 'react';
import type { Tokens } from './tokens';

type Variant = 'ai' | 'info' | 'success' | 'warning';

export interface AISuggestionStripProps {
  title: string;
  body: string;
  ctaLabel?: string;
  dismissLabel?: string;
  onAccept?: () => void;
  onDismiss?: () => void;
  variant?: Variant;
  icon?: ReactNode;
}

function pickAccent(t: Tokens, variant: Variant): string {
  switch (variant) {
    case 'info':
      return t.primary;
    case 'success':
      return t.success;
    case 'warning':
      return t.warning;
    case 'ai':
    default:
      return t.ai;
  }
}

export function AISuggestionStrip({
  title,
  body,
  ctaLabel = 'Accetta',
  dismissLabel = 'Ignora',
  onAccept,
  onDismiss,
  variant = 'ai',
  icon,
}: AISuggestionStripProps) {
  const { t } = useAuroraTheme();
  const accent = pickAccent(t, variant);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="flex items-start gap-3 p-3 pr-4 rounded-xl relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${accent}12 0%, transparent 60%)`,
        border: `1px solid ${accent}30`,
      }}
    >
      <div
        className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
        style={{ background: `${accent}22`, color: accent }}
      >
        {icon ?? <Sparkles className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium" style={{ color: t.text }}>
          {title}
        </div>
        <div className="text-[12.5px] mt-0.5" style={{ color: t.muted }}>
          {body}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {onAccept && (
          <button
            type="button"
            onClick={onAccept}
            className="text-[12px] px-3 py-1.5 rounded-md font-medium transition-opacity hover:opacity-90"
            style={{ background: accent, color: '#FFFFFF' }}
          >
            {ctaLabel}
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-[12px] px-2 py-1.5 rounded-md inline-flex items-center gap-1 transition-colors"
            style={{ color: t.muted }}
            aria-label={dismissLabel}
          >
            <X className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{dismissLabel}</span>
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default AISuggestionStrip;
